import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Why a participant cannot open a material right now (null = they can).
 * Decided by the get_my_materials() RPC on the server: payment, ticket, then
 * the availability window against the database clock.
 */
export type MaterialLockReason = 'payment' | 'ticket' | 'upcoming' | 'closed' | null;

/** One row of get_my_materials(). Links are null whenever lock_reason is set. */
export interface MyMaterial {
  id: string;
  content_id: string;
  content_title: string;
  title: string;
  description: string | null;
  type: string;
  file_url: string | null;
  external_url: string | null;
  is_downloadable: boolean | null;
  sort_order: number | null;
  created_at: string | null;
  opens_at: string | null;
  closes_at: string | null;
  lock_reason: MaterialLockReason;
  total_count: number;
}

interface MyMaterialsOptions {
  contentId?: string;
  /** Drop materials locked by payment or ticket (the "my materials" list). */
  hideNotIncluded?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * The signed-in participant's materials. content_materials itself is readable
 * only by organizers; participants always go through this RPC.
 */
export async function fetchMyMaterials(
  supabase: SupabaseClient,
  { contentId, hideNotIncluded = false, limit, offset = 0 }: MyMaterialsOptions = {},
): Promise<MyMaterial[]> {
  const { data, error } = await supabase.rpc('get_my_materials' as any, {
    p_content_id: contentId ?? null,
    p_hide_not_included: hideNotIncluded,
    p_limit: limit ?? null,
    p_offset: offset,
  });
  if (error) throw error;
  return (data ?? []) as MyMaterial[];
}
